/**
 * CallRecorder - Client-side WebRTC call recorder with audio mixing and dual-video canvas compositor.
 * Produces high-quality, synchronized recordings containing both participants' video and audio.
 */

function drawRoundedRect(ctx, x, y, width, height, radius) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

function drawVideoCover(ctx, video, targetX, targetY, targetW, targetH, radius = 16) {
  if (!video || video.videoWidth === 0 || video.videoHeight === 0) return false;

  ctx.save();
  drawRoundedRect(ctx, targetX, targetY, targetW, targetH, radius);
  ctx.clip();

  const vw = video.videoWidth;
  const vh = video.videoHeight;
  const targetRatio = targetW / targetH;
  const videoRatio = vw / vh;

  let sx, sy, sw, sh;
  if (videoRatio > targetRatio) {
    // Video is wider than target
    sh = vh;
    sw = vh * targetRatio;
    sx = (vw - sw) / 2;
    sy = 0;
  } else {
    // Video is taller than target
    sw = vw;
    sh = vw / targetRatio;
    sx = 0;
    sy = (vh - sh) / 2;
  }

  try {
    ctx.drawImage(video, sx, sy, sw, sh, targetX, targetY, targetW, targetH);
  } catch {
    ctx.restore();
    return false;
  }
  ctx.restore();
  return true;
}

function drawAvatar(ctx, name, targetX, targetY, targetW, targetH, radius = 16) {
  ctx.save();
  drawRoundedRect(ctx, targetX, targetY, targetW, targetH, radius);
  ctx.fillStyle = '#1e293b'; // slate-800
  ctx.fill();
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
  ctx.lineWidth = 1;
  ctx.stroke();

  const cx = targetX + targetW / 2;
  const cy = targetY + targetH / 2;
  const avatarRadius = Math.min(targetW, targetH) * 0.18;

  // Avatar circle
  ctx.beginPath();
  ctx.arc(cx, cy - 20, avatarRadius, 0, Math.PI * 2);
  ctx.fillStyle = '#3b82f6'; // primary blue
  ctx.fill();

  // Initials
  const initial = (name || 'U').trim().charAt(0).toUpperCase();
  ctx.fillStyle = '#ffffff';
  ctx.font = `bold ${Math.round(avatarRadius * 0.9)}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(initial, cx, cy - 20);

  // Subtitle
  ctx.fillStyle = '#94a3b8'; // slate-400
  ctx.font = `500 16px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
  ctx.fillText('Камера выключена', cx, cy + avatarRadius + 15);

  ctx.restore();
}

function drawNamePill(ctx, name, x, y) {
  ctx.save();
  ctx.font = '600 15px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
  const text = name || 'Участник';
  const metrics = ctx.measureText(text);
  const pillW = metrics.width + 24;
  const pillH = 32;

  drawRoundedRect(ctx, x, y - pillH, pillW, pillH, 8);
  ctx.fillStyle = 'rgba(15, 23, 42, 0.85)';
  ctx.fill();
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
  ctx.lineWidth = 1;
  ctx.stroke();

  ctx.fillStyle = '#ffffff';
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.fillText(text, x + 12, y - pillH / 2);
  ctx.restore();
}

export class CallRecorder {
  constructor() {
    this.recorder = null;
    this.chunks = [];
    this.audioCtx = null;
    this.canvas = null;
    this.ctx = null;
    this.active = false;
    this.worker = null;
    this.workerUrl = null;
    this.localVideo = null;
    this.remoteVideo = null;
    this.localStream = null;
    this.remoteStream = null;
    this.localName = 'Оператор';
    this.remoteName = 'Клиент';
    this.canvasStream = null;
  }

  /**
   * Start recording with mixed audio and composited dual video
   * @param {object} opts
   * @param {MediaStream} [opts.localStream]
   * @param {MediaStream} [opts.remoteStream]
   * @param {string} [opts.localName]
   * @param {string} [opts.remoteName]
   */
  start({ localStream, remoteStream, localName = 'Оператор', remoteName = 'Клиент' } = {}) {
    if (this.active) return;
    this.active = true;
    this.chunks = [];
    this.localStream = localStream || null;
    this.remoteStream = remoteStream || null;
    this.localName = localName;
    this.remoteName = remoteName;

    // 1. Offscreen canvas setup (1280x720 HD)
    this.canvas = document.createElement('canvas');
    this.canvas.width = 1280;
    this.canvas.height = 720;
    this.ctx = this.canvas.getContext('2d', { alpha: false });

    // 2. Offscreen video elements for smooth canvas texture sampling
    this.localVideo = document.createElement('video');
    this.localVideo.muted = true;
    this.localVideo.playsInline = true;
    this.localVideo.autoplay = true;
    if (this.localStream) {
      this.localVideo.srcObject = this.localStream;
      this.localVideo.play().catch(() => {});
    }

    this.remoteVideo = document.createElement('video');
    this.remoteVideo.muted = true;
    this.remoteVideo.playsInline = true;
    this.remoteVideo.autoplay = true;
    if (this.remoteStream) {
      this.remoteVideo.srcObject = this.remoteStream;
      this.remoteVideo.play().catch(() => {});
    }

    // 3. AudioContext for multi-participant mixing
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    this.audioCtx = new AudioContextClass();
    if (this.audioCtx.state === 'suspended') {
      this.audioCtx.resume().catch(() => {});
    }
    const audioDest = this.audioCtx.createMediaStreamDestination();

    if (this.localStream && this.localStream.getAudioTracks().length > 0) {
      try {
        const localAudioStream = new MediaStream([this.localStream.getAudioTracks()[0]]);
        const localSource = this.audioCtx.createMediaStreamSource(localAudioStream);
        localSource.connect(audioDest);
      } catch (err) {
        console.warn('Local audio source error:', err);
      }
    }

    if (this.remoteStream && this.remoteStream.getAudioTracks().length > 0) {
      try {
        const remoteAudioStream = new MediaStream([this.remoteStream.getAudioTracks()[0]]);
        const remoteSource = this.audioCtx.createMediaStreamSource(remoteAudioStream);
        remoteSource.connect(audioDest);
      } catch (err) {
        console.warn('Remote audio source error:', err);
      }
    }

    // 4. Background-immune Web Worker for constant 30 FPS rendering
    const workerCode = `
      let timer = null;
      self.onmessage = function(e) {
        if (e.data === 'start') {
          timer = setInterval(function() { self.postMessage('tick'); }, 1000 / 30);
        } else if (e.data === 'stop') {
          clearInterval(timer);
        }
      };
    `;
    const blob = new Blob([workerCode], { type: 'application/javascript' });
    this.workerUrl = URL.createObjectURL(blob);
    this.worker = new Worker(this.workerUrl);
    this.worker.onmessage = () => {
      if (this.active) this.renderFrame();
    };
    this.worker.postMessage('start');

    // 5. Canvas capture stream
    try {
      this.canvasStream = this.canvas.captureStream(30);
    } catch (err) {
      console.warn('captureStream error:', err);
    }

    // 6. Build the composite MediaStream (video + mixed audio)
    const tracks = [];
    if (this.canvasStream && this.canvasStream.getVideoTracks().length > 0) {
      tracks.push(this.canvasStream.getVideoTracks()[0]);
    } else if (this.remoteStream?.getVideoTracks()[0]) {
      tracks.push(this.remoteStream.getVideoTracks()[0]);
    } else if (this.localStream?.getVideoTracks()[0]) {
      tracks.push(this.localStream.getVideoTracks()[0]);
    }

    if (audioDest.stream.getAudioTracks().length > 0) {
      tracks.push(audioDest.stream.getAudioTracks()[0]);
    }

    const combinedStream = new MediaStream(tracks);

    // 7. Initialize MediaRecorder
    let mimeType = 'video/webm;codecs=vp8,opus';
    if (typeof MediaRecorder !== 'undefined') {
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'video/webm;codecs=vp9,opus';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'video/webm';
          if (!MediaRecorder.isTypeSupported(mimeType)) {
            mimeType = '';
          }
        }
      }
    }

    try {
      this.recorder = new MediaRecorder(combinedStream, mimeType ? { mimeType } : undefined);
    } catch (err) {
      console.warn('MediaRecorder init with mimeType failed, falling back to default:', err);
      this.recorder = new MediaRecorder(combinedStream);
    }

    this.recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) {
        this.chunks.push(e.data);
      }
    };

    // Request data in chunks every 1000ms to avoid memory buildup
    this.recorder.start(1000);
  }

  /**
   * Update live streams dynamically during a call
   */
  updateStreams({ localStream, remoteStream, localName, remoteName } = {}) {
    if (localStream && localStream !== this.localStream) {
      this.localStream = localStream;
      if (this.localVideo) {
        this.localVideo.srcObject = localStream;
        this.localVideo.play().catch(() => {});
      }
    }
    if (remoteStream && remoteStream !== this.remoteStream) {
      this.remoteStream = remoteStream;
      if (this.remoteVideo) {
        this.remoteVideo.srcObject = remoteStream;
        this.remoteVideo.play().catch(() => {});
      }
    }
    if (localName) this.localName = localName;
    if (remoteName) this.remoteName = remoteName;
  }

  /**
   * Render single frame onto offscreen canvas
   */
  renderFrame() {
    if (!this.ctx || !this.canvas) return;
    const ctx = this.ctx;
    const w = this.canvas.width;
    const h = this.canvas.height;

    // Dark canvas background
    ctx.fillStyle = '#090d16';
    ctx.fillRect(0, 0, w, h);

    const hasLocal = Boolean(
      this.localStream &&
      this.localStream.getVideoTracks().length > 0 &&
      this.localStream.getVideoTracks()[0].enabled
    );
    const hasRemote = Boolean(
      this.remoteStream &&
      this.remoteStream.getVideoTracks().length > 0 &&
      this.remoteStream.getVideoTracks()[0].enabled
    );

    const pad = 16;
    const topBarH = 50;

    // Top Header / Call Branding
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.font = 'bold 15px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText('Запись видеозвонка', pad, topBarH / 2);

    // Live Recording Indicator dot
    ctx.beginPath();
    ctx.arc(w - pad - 12, topBarH / 2, 6, 0, Math.PI * 2);
    ctx.fillStyle = '#ef4444';
    ctx.fill();

    ctx.fillStyle = '#ef4444';
    ctx.font = '600 13px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText('REC', w - pad - 26, topBarH / 2);

    const bodyY = topBarH;
    const bodyH = h - topBarH - pad;

    if (this.remoteStream && this.localStream) {
      // Dual layout: Client on the left, Operator on the right
      const slotW = (w - pad * 3) / 2;

      // Left: Client (Remote)
      const rX = pad;
      const rY = bodyY;
      const remoteDrawn = hasRemote && drawVideoCover(ctx, this.remoteVideo, rX, rY, slotW, bodyH, 16);
      if (!remoteDrawn) {
        drawAvatar(ctx, this.remoteName, rX, rY, slotW, bodyH, 16);
      }
      drawNamePill(ctx, this.remoteName, rX + 16, rY + bodyH - 16);

      // Right: Operator (Local)
      const lX = pad * 2 + slotW;
      const lY = bodyY;
      const localDrawn = hasLocal && drawVideoCover(ctx, this.localVideo, lX, lY, slotW, bodyH, 16);
      if (!localDrawn) {
        drawAvatar(ctx, this.localName, lX, lY, slotW, bodyH, 16);
      }
      drawNamePill(ctx, this.localName, lX + 16, lY + bodyH - 16);

    } else if (this.remoteStream) {
      // Remote only
      const slotW = w - pad * 2;
      const remoteDrawn = hasRemote && drawVideoCover(ctx, this.remoteVideo, pad, bodyY, slotW, bodyH, 16);
      if (!remoteDrawn) {
        drawAvatar(ctx, this.remoteName, pad, bodyY, slotW, bodyH, 16);
      }
      drawNamePill(ctx, this.remoteName, pad + 16, bodyY + bodyH - 16);

    } else {
      // Local only
      const slotW = w - pad * 2;
      const localDrawn = hasLocal && drawVideoCover(ctx, this.localVideo, pad, bodyY, slotW, bodyH, 16);
      if (!localDrawn) {
        drawAvatar(ctx, this.localName, pad, bodyY, slotW, bodyH, 16);
      }
      drawNamePill(ctx, this.localName, pad + 16, bodyY + bodyH - 16);
    }
  }

  /**
   * Stop recording and return recorded Blob
   * @returns {Promise<Blob|null>}
   */
  stop() {
    return new Promise((resolve) => {
      this.active = false;

      if (!this.recorder || this.recorder.state === 'inactive') {
        this.cleanup();
        resolve(null);
        return;
      }

      this.recorder.onstop = () => {
        const mimeType = this.recorder?.mimeType || 'video/webm';
        const blob = new Blob(this.chunks, { type: mimeType });
        this.cleanup();
        resolve(blob);
      };

      try {
        this.recorder.stop();
      } catch (err) {
        console.warn('Error calling recorder.stop():', err);
        this.cleanup();
        resolve(null);
      }
    });
  }

  cleanup() {
    this.active = false;
    if (this.worker) {
      this.worker.postMessage('stop');
      this.worker.terminate();
      this.worker = null;
    }
    if (this.workerUrl) {
      URL.revokeObjectURL(this.workerUrl);
      this.workerUrl = null;
    }
    if (this.canvasStream) {
      this.canvasStream.getTracks().forEach((t) => t.stop());
      this.canvasStream = null;
    }
    if (this.audioCtx) {
      this.audioCtx.close().catch(() => {});
      this.audioCtx = null;
    }
    if (this.localVideo) {
      this.localVideo.srcObject = null;
      this.localVideo = null;
    }
    if (this.remoteVideo) {
      this.remoteVideo.srcObject = null;
      this.remoteVideo = null;
    }
    this.canvas = null;
    this.ctx = null;
  }
}
